import appInfo from "@common/appInfo";
import { getDefaultValues, mapFieldValue, parseSource } from "@common/controls/Form/DynamicForm/lib";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import { type DateRangeValue } from "@common/controls/Inputs/DateRangePicker/component";
import { type OptionItem } from "@common/controls/Inputs/Select/component";
import { type GenericMap, System } from "@common/declarations";
import { sort } from "@common/lib/array";
import date from "@common/lib/date";
import { type I18n } from "@common/lib/i18n";
import { fromJson } from "@common/lib/json";
import { isVisible } from "@common/lib/misc";
import { type Data } from "@common/modules/data/types";
import { type FormField } from "@common/modules/system/types";
import React from "react";
import { type BadgeInfo, type GetBadgesArgs } from "./types";

export const mapFormFieldToDynamicFormField = ({
  formFieldId,
  formFieldKey,
  ...x
}: Data.FormField): DynamicForms.Field => ({
  ...x,
  name: x.formControlKey,
  value: mapFieldValue(x.plainTextValue, x.formControlKey, x.dataType)
});

export const collapseReportParameters = (parameters: GenericMap<any>): GenericMap<any> => {
  const parametersCopy = { ...parameters };
  while (true) {
    const start = Object.keys(parametersCopy).find((k) => k.startsWith("Start"));
    if (start) {
      const name = start.replace(/^Start/, "");
      const end = Object.keys(parametersCopy).find((k) => k === `End${name}`);
      if (end) {
        parametersCopy[name] = {
          from: new Date(parametersCopy[start]),
          to: new Date(parametersCopy[end])
        };

        delete parametersCopy[start];
        delete parametersCopy[end];

        continue;
      }
    }
    break;
  }
  return parametersCopy;
};

export const expandReportParameters = (fields: System.FormField[], parameters: GenericMap<any>): GenericMap<any> =>
  Object.keys(parameters).reduce((res, x) => {
    if (!parameters[x]) return res;

    const f = fields.find((y) => y.formFieldKey === x);

    if (!isVisible(f?.visibilityCondition, parameters)) return res;

    switch (f?.formControlKey) {
      case "DateRange": {
        const range = typeof parameters[x] === "string" ? JSON.parse(parameters[x]) : parameters[x];
        return {
          ...res,
          [`Start${x}`]: range.from,
          [`End${x}`]: range.to
        };
      }
    }

    return {
      ...res,
      [x]: parameters[x]
    };
  }, {});

export const getFinalReportParameters = (fields: System.FormField[], parameters: GenericMap<any>): GenericMap<any> =>
  Object.keys(parameters).reduce((res, x) => {
    const field = fields.find((y) => y.formFieldKey === x)!;

    let value = parameters[x];
    if (field.formControlKey === "Multiselect" && value) {
      value = value.split(",").filter((x) => x); // note: we set output format to string in the component. The API always expects an array
    }

    return {
      ...res,
      [x]: value
    };
  }, {});

export const populateBadgeData = async (
  i18n: I18n,
  fields: System.FormField[],
  parameters: GenericMap<any>
): Promise<GenericMap<any>> => {
  const sources = await Promise.all(
    fields
      .map((x) => parseSource(i18n, x.source, x.options, {}))
      .map((x) => {
        if (!x || x.isBePagination || (!x.fetchOptions && !x.options)) return Promise.resolve(null);

        return x.fetchOptions ? x.fetchOptions({ page: 1, size: 1, search: "" }) : Promise.resolve(x.options!);
      })
  );

  return fields.reduce((res, x, i) => {
    const pv = parameters[x.formFieldKey!] ?? x.value;

    const value = sources[i]
      ? (pv as string)
          .split(",")
          .map((x) => (sources[i] as OptionItem<any>[]).find((y) => y.value == x)?.label)
          .join(", ")
      : pv;

    return { ...res, [x.formFieldKey!]: value };
  }, {});
};

const getBadgeLabel = (i18n: I18n, field: FormField, parameters: GenericMap<any>, filters: Data.Filter[]): any => {
  const value = parameters[field.formFieldKey!] ?? field.plainTextValue;
  if (!value) return value;

  switch (field.formControlKey) {
    case "Date": {
      return date.formatDate(value);
    }
    case "DateRange": {
      const v = typeof value === "object" ? (value as DateRangeValue) : fromJson<DateRangeValue>(value)!;
      return (
        <span>
          {date.formatDate(v.from)} - {date.formatDate(v.to)}
        </span>
      );
    }
    case "DateTime": {
      return date.formatDateTime(value);
    }
    case "Select":
    case "Multiselect":
    case "Autocomplete": {
      const fv = filters.find((x) => x.key === field.formFieldKey);
      const displayLabel = fv?.displayLabel ?? (field.source!.startsWith("json") ? value : field.plainTextLabel);
      const displayName = fv?.displayName ?? (field.source!.startsWith("json") ? value : field.plainTextName);

      return displayLabel
        ? displayLabel
            .split(",")
            .map((x) => i18n[x])
            .join(", ")
        : displayName
        ? displayName.split(",").join(", ")
        : value;
    }
    default: {
      if (value === "@CurrentUserID") return appInfo.user?.displayName;

      return (
        filters.find((x) => x.key === field.formFieldKey)?.displayLabel ??
        value ??
        field.plainTextLabel ??
        field.plainTextName
      );
    }
  }
};

export const getReportBadges = (
  i18n: I18n,
  { badges, fields, drillDownInfo, filters, onBadgeClick, parameters }: GetBadgesArgs
) => {
  const values = getDefaultValues(
    fields.map((x) => ({
      ...x,
      name: x.formFieldKey!,
      value: x.formFieldKey! in parameters ? parameters[x.formFieldKey!] : x.plainTextValue
    }))
  );
  return [
    ...drillDownInfo.map<BadgeInfo>((x) => ({
      key: x.key,
      label: x.label,
      valueLabel: x.valueLabel
    })),
    ...sort(fields, { key: "sortOrder" })
      .filter((x) => {
        if (!values[x.formFieldKey!]) return false;

        if (!isVisible(x.visibilityCondition, values)) return false;

        const key = x.formControlKey === "DateRange" ? `Start${x.formFieldKey}` : x.formFieldKey;

        return key ? badges.includes(key) : false;
      })
      .map<BadgeInfo>((x) => ({
        key: x.formFieldKey!,
        label: x.label ?? x.name ?? x.formFieldKey!,
        valueLabel: getBadgeLabel(i18n, x, parameters, filters),
        onClick: onBadgeClick
      }))
  ];
};
